import React from "react";
import classNames from "classnames";

import { checkFullPath, isMobileMode } from "../../../utils";
import { Fa } from "../../atoms/Icons/Fa";
import { ProgressiveImage } from "../../atoms/ProgressiveImage/ProgressiveImage";
import { Membership } from "../../atoms/Icons/MembershipIcon";
import { SpainIcon } from "../../atoms/Icons/SpainIcon";
import { MobileQuery } from "../../atoms/Layout/Responsive";
import { H1 } from "../../atoms/Text/Headings";
import { Text } from "../../atoms/Text/Text";
import { Game } from "../../models/Game";
import styles from "./GameDescription.css";
import { RewardBadge } from "../RewardBadge/RewardBadge";
import { getBadgeByGame } from "../../constants/Badges";

type GameDescriptionProps = {
  game: Game;
};

export class GameDescription extends React.PureComponent<GameDescriptionProps> {
  state = {
    isOpened: true,
  };

  onOpenClick = () =>
    this.setState({
      isOpened: !this.state.isOpened,
    });

  render() {
    const { game } = this.props;

    const shouldShowContent = isMobileMode() ? this.state.isOpened : true;

    const title = Game.getOnPageTitle(game);

    const badge = getBadgeByGame(game.slug);

    const roleAttribute = isMobileMode() ? { role: "button" } : null;

    return (
      <Container data-element-description="game description">
        <Description className={game.membersOnly ? styles.membersOnly : ""}>
          <GameDescriptionHeader
            aria-label="Open Game Description Button"
            onClick={this.onOpenClick}
            {...roleAttribute}
          >
            {badge ? <RewardBadge badge={badge} /> : null}
            {game.isSpain() ? <SpainSign /> : null}
            <GameName>
              <TitleText
                content={game.seo_OnPageTitleInnerHtml}
                isDangerously={game.seo_OnPageTitleInnerHtml}
              >
                {title}
              </TitleText>

              <MobileQuery>
                {this.state.isOpened ? (
                  <Text
                    key="opened"
                    ariaExpanded="true"
                    ariaLabel="open Game Description"
                  >
                    <Fa className={styles.openButtonIcon} icon="angle-up" />
                  </Text>
                ) : (
                  <Text
                    key="closed"
                    ariaExpanded="false"
                    ariaLabel="close Game Description"
                  >
                    <Fa className={styles.openButtonIcon} icon="angle-down" />
                  </Text>
                )}
              </MobileQuery>
            </GameName>
          </GameDescriptionHeader>

          <DescriptionText
            size="sm"
            visible={shouldShowContent}
            content={game.seo_OnPageDescription || game.description}
            isDangerously={game.seo_IsInnerHtmlOnPageDescription}
          >
            {game.seo_OnPageDescription || game.description}
          </DescriptionText>
          {game.membersOnly ? <MemberSign size="middle" /> : null}
        </Description>

        {(game.seo_isScreenshot === undefined || game.seo_isScreenshot) && (
          <ScreenshotContainer visible={shouldShowContent}>
            <ProgressiveImage
              title={`Enjoy playing ${title}`}
              alt={title}
              image={checkFullPath(game.assetOriginUrl, game.screenshotPath)}
              useParentSize={true}
              loadWhenViewable={true}
            />
          </ScreenshotContainer>
        )}
      </Container>
    );
  }
}

const ScreenshotContainer = ({ visible, ...props }: any) => (
  <div
    className={classNames(styles.screenshotContainer, {
      [styles.visible]: !!visible,
    })}
    {...props}
  />
);

const Container = (props: any) => (
  <div className={styles.container} {...props} />
);

const GameDescriptionHeader = (props: any) => (
  <div className={styles.gameDescriptionHeader} {...props} />
);

const Description = (props: any) => (
  <div className={styles.description} {...props} />
);

const GameName = (props: any) => <H1 className={styles.gameName} {...props} />;

const DescriptionText = (props: any) => (
  <Text
    className={classNames(styles.descriptionText, {
      [styles.visible]: !!props.visible,
    })}
    {...props}
  />
);

const TitleText = (props: any) => <Text {...props} />;

const MemberSign = (props: any) => <Membership {...props} />;
const SpainSign = (props: any) => (
  <SpainIcon className={styles.spainSign} {...props} />
);
